import { RunRewardController, type RewardAward } from '../../app/RunRewardController';
import { phaseRewardRange } from '../../game/gacha/gachaRules';
import type { SessionState } from '../../game/simulation/types';
import { DEFAULT_GAME_MODE, getGameMode, phaseName } from '../../game/content/modes';

const number = (value: number) => value.toLocaleString('ja-JP');

/** Shows already saved earnings; displaying a result never awards currency. */
export class RunRewardsUI {
  private state: SessionState | null = null;
  private readonly hud = document.createElement('section');
  private readonly result = document.createElement('section');
  private readonly toast = document.createElement('div');
  private readonly retryBanner = document.createElement('div');
  private readonly unsubscribe: () => void;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(root: HTMLElement, private readonly dialog: HTMLDialogElement, private readonly rewards: RunRewardController,
    onGacha: () => void, retry: () => Promise<void>) {
    this.hud.className = 'run-reward-hud';
    this.hud.setAttribute('aria-label', '今回の観測石');
    this.hud.innerHTML = '<div><span>今回の観測石</span><strong data-run-stones>0</strong></div><p data-run-next></p><p data-run-save role="status" hidden></p><button type="button" class="economy-text-button" data-reward-retry hidden>報酬の保存を再試行</button>';
    root.querySelector('.hud-panel--left .metric-row')!.after(this.hud);
    this.result.className = 'run-reward-result';
    this.result.innerHTML = '<div><span>今回の獲得観測石</span><strong data-run-stones>0</strong></div><p data-run-breakdown></p><p data-run-save role="status"></p><button type="button" class="economy-text-button" data-reward-retry hidden>報酬の保存を再試行</button>';
    root.querySelector('.result-grid')!.after(this.result);
    const gacha = document.createElement('button');
    gacha.type = 'button'; gacha.className = 'ranking-link-button'; gacha.id = 'result-gacha-button';
    gacha.textContent = '観測石でガチャを引く'; gacha.addEventListener('click', onGacha);
    root.querySelector('#result-restart-button')!.after(gacha);
    this.toast.className = 'phase-reward-toast'; this.toast.hidden = true;
    this.toast.setAttribute('role', 'status'); this.toast.setAttribute('aria-live', 'polite');
    root.querySelector('.playfield-frame')!.append(this.toast);
    this.retryBanner.className = 'run-reward-retry'; this.retryBanner.hidden = true;
    this.retryBanner.innerHTML = '<span>報酬の確認・保存を再試行してください。</span><button type="button" class="economy-text-button" data-reward-retry>報酬を再確認</button>';
    dialog.querySelector('.economy-header')!.after(this.retryBanner);
    for (const element of [this.hud, this.result, this.retryBanner]) {
      element.querySelector<HTMLButtonElement>('[data-reward-retry]')!.addEventListener('click', () => { void retry(); });
    }
    this.unsubscribe = rewards.onChange((id, award) => {
      this.render();
      if (award && this.state?.runId === id) this.showAward(award);
    });
  }
  updateState(state: SessionState): void {
    const previous = this.state;
    this.state = state;
    if (previous?.runId !== state.runId) { clearTimeout(this.timer); this.toast.hidden = true; }
    if (previous?.runId !== state.runId || previous.phase !== state.phase || previous.clearedThisRun !== state.clearedThisRun) this.render();
  }
  render(): void {
    const state = this.state;
    if (!state) return;
    const reward = this.rewards.get(state.runId);
    const pending = reward.saving || reward.clearSaving;
    const failed = Boolean(reward.error || reward.clearError || reward.loadError);
    const status = reward.loadError ? '報酬を読み込めませんでした。再試行してください。'
      : failed ? '一部の報酬が未保存です。再試行してください。'
      : pending ? '報酬を保存中… 表示額は保存済みの分です。'
      : this.rewards.hasErrors ? '前のプレイの報酬に再確認が必要です。'
      : reward.total > 0 ? '所持観測石に反映済みです。' : '第二共鳴に到達すると100〜150個もらえます。';
    for (const element of [this.hud, this.result]) {
      element.querySelector('[data-run-stones]')!.textContent = number(reward.total);
      const save = element.querySelector<HTMLElement>('[data-run-save]')!;
      save.textContent = status; save.hidden = element === this.hud && !pending && !failed && !this.rewards.hasErrors;
      element.querySelector<HTMLElement>('[data-reward-retry]')!.hidden = !this.rewards.hasErrors;
    }
    const next = state.phase < getGameMode(state.mode).phases.length ? phaseRewardRange(state.phase + 1) : null;
    this.hud.querySelector('[data-run-next]')!.textContent = state.clearedThisRun ? 'クリア報酬を含む獲得合計'
      : next ? `${phaseName(state.mode, state.phase + 1)}で +${number(next.min)}〜${number(next.max)}個` : 'クリアでさらに +100〜9,000個';
    this.result.querySelector('[data-run-breakdown]')!.textContent = `共鳴到達 ${number(reward.ledger?.amount ?? 0)}個${state.clearedThisRun ? ` ＋ クリア ${number(reward.clearAmount)}個` : ''}`;
    this.retryBanner.hidden = !this.rewards.hasErrors;
    const summary = this.dialog.querySelector<HTMLElement>('[data-clear-run-summary]');
    if (summary?.dataset.runId) {
      const saved = this.rewards.get(summary.dataset.runId);
      summary.textContent = saved.loading ? '今回の獲得合計を確認中…' : saved.loadError ? '獲得合計を読み込めませんでした。再試行してください。'
        : `共鳴到達 ${number(saved.ledger?.amount ?? 0)}個 ＋ クリア ${number(saved.clearAmount)}個 ＝ 今回の獲得 ${number(saved.total)}個${saved.saving || saved.error ? '（到達報酬の一部は未保存）' : '（反映済み）'}`;
    }
  }
  private showAward(award: RewardAward): void {
    clearTimeout(this.timer);
    this.toast.replaceChildren();
    const heading = document.createElement('span'); heading.textContent = `${phaseName(this.state?.mode ?? DEFAULT_GAME_MODE, award.phase)}の報酬`;
    const amount = document.createElement('strong'); amount.textContent = `観測石 +${number(award.amount)}`;
    const detail = document.createElement('small'); detail.textContent = `基本 ${number(award.minimum)} ＋ ランダム ${number(award.amount - award.minimum)}`;
    this.toast.append(heading, amount, detail); this.toast.hidden = false;
    this.timer = setTimeout(() => { this.toast.hidden = true; }, 3800);
  }
  destroy(): void { this.unsubscribe(); clearTimeout(this.timer); }
}
